import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useTranslation } from 'react-i18next';
import { required, shape } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation, isValid } from '@totalsoft/pure-validations-react';
import { useToast } from 'hooks/toasts/useToast';
import { Card, IconButton } from '@totalsoft/rocket-ui';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { omit } from 'ramda';
import AccountStatementGeneralDataComponent from './AccountStatementGeneralDataComponent';
import { ACCOUNT_STATEMENT_UPDATED } from '../../subscriptions';
import { ADD_UPDATE_ACCOUNT_STATEMENT } from '../../mutations';
import { ACCOUNT_STATEMENT_QUERY, BANKS_SEARCH } from '../../queries';
import { isDirty } from '@totalsoft/change-tracking';
import { COMMAND_EXECUTION_ERROR } from '../../../../common/components/subscriptions/GlobalSubscription';
import { useParams } from 'hooks';
import { BankTypeId } from 'constants/paramCodes';

const defaultAccountStatement = {
  configId: null,
  configName: '',
  activeYN: false
};

const accountStatementValidator = shape({
  configName: required
});

const AccountStatementGeneralDataContainer = ({ canWrite, configId, onRedirect }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultAccountStatement);
  const addToast = useToast();
  const [saving, setSaving] = useState(false);
  const [t] = useTranslation();
  const [accountStatementValidation, validateAccountStatement] = useDirtyFieldValidation(accountStatementValidator);
  const bankTypeId = useParams(BankTypeId);

  const [bankInputValue, setBankInputValue] = useState("");
  const [bankInputQueryValue, setBankInputQueryValue] = useState("");
  
  const { data: banksData, loading: banksLoading } = useQuery(BANKS_SEARCH, {
    variables: { search: bankInputQueryValue, partnerTypeId: bankTypeId }
  });
  
  const banksOptions = useMemo(
    () => banksData?.getPartnersSearchByUniqueIdAndTypeId.map(x => ({ id: x.id, partnerName: x.partnerName })) || [],
    [banksData?.getPartnersSearchByUniqueIdAndTypeId]
  );
  
  const handleBankInputChange = useCallback((_, value, reason) => {
    if (reason !== "reset") {
      setBankInputQueryValue(value);
    }
    setBankInputValue(value);
  }, []);

  useQuery(ACCOUNT_STATEMENT_QUERY, {
    onCompleted: data => {
      resetLens(omit(['accountStatementIndicators'], data.accountStatement));
    },
    variables: { configId },
    skip: !configId
  });

  useEffect(() => {
    validateAccountStatement(get(lens), dirtyInfo);
  }, [validateAccountStatement, lens, dirtyInfo]);

  const [addOrUpdateAccountStatement] = useMutation(ADD_UPDATE_ACCOUNT_STATEMENT, {
    onError: () => {
      setSaving(false);
    }
  });

  useSubscription(ACCOUNT_STATEMENT_UPDATED, {
    onData: ({
      data: {
        data: { accountStatementUpdated }
      },
      client
    }) => {
      if (!configId) onRedirect(accountStatementUpdated.accountStatement.configId);
      setSaving(false);

      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'accountStatements' });
      client.cache.gc();
      addToast(t('AccountStatement.AccountStatementUpdated'), 'success');
    }
  });

  useSubscription(COMMAND_EXECUTION_ERROR, {
    onData: data => {
      const messageCode = data?.data?.data?.commandExecutionError?.code;
      const messageTranslated = t(messageCode);
      addToast(
        t('General.CommandExecutionError', { message: messageTranslated !== messageCode ? messageTranslated : messageCode }),
        'error'
      );
    }
  });

  const handleSave = useCallback(() => {
    const accountStatement = lens |> get;
    if (!validateAccountStatement(accountStatement)) return;
    setSaving(true);
    addOrUpdateAccountStatement({
      variables: {
        input: {
          ...omit(['usedByPartner'], accountStatement),
          configVersion: accountStatement.configId ? accountStatement.configVersion : 1
        }
      }
    });
  }, [lens, addOrUpdateAccountStatement, validateAccountStatement]);

  return (
    <Card
      icon={AccountBalanceIcon}
      iconColor='primary'
      title={t('AccountStatement.GeneralData')}
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('SaveChanges')}
          onClick={handleSave}
          disabled={!canWrite || !isDirty(dirtyInfo) || !isValid(accountStatementValidation)}
        />
      }
    >
      <AccountStatementGeneralDataComponent
        accountStatement={lens}
        validation={accountStatementValidation}
        canWrite={canWrite}
        saving={saving}
        bankInputValue={bankInputValue}
        handleBankInputChange={handleBankInputChange}
        banksOptions={banksOptions}
        banksLoading={banksLoading}
      />
    </Card>
  );
};

AccountStatementGeneralDataContainer.propTypes = {
  canWrite: PropTypes.bool,
  accountStatement: PropTypes.object,
  configId: PropTypes.number,
  onRedirect: PropTypes.func
};

export default AccountStatementGeneralDataContainer;
